import {
    Box,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    useParams,
} from '../../utils/globalImports';

import {
    useState,
    CustomButton,
    CustomDatePicker,
    CustomTextField,
    Dayjs,
    CustomConfirmDialog,
    useEffect,
    CustomBackdrop,
    validationMessages,
    ProjectPayment
} from '../../utils/globalImports';

import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore);


interface AFPTableProps {
    data: ProjectPayment;
    endDate: string;
    onSaveInterval: (payload: any) => void;  // Callback for editing a row
}

interface DynamicRow {
    submissionRows: string[];
    certificationRows: string[];
    expectedRows: string[];
}

interface FormData {
    submissionDate: string | null;
    certificationDate: string | null;
    expectedDate: string | null;
    submissionInterval: number;
    certificationInterval: number;
    expectedInterval: number;
}

export interface PaymentRequest {
    projectId: number;
    submissionDates: Submission[];
    certificationDates: Certification[];
    expectedPaymentDates: Expected[];
    submissionInterval: number;
    certificationInterval: number,
    expectedInterval: number,
}

interface Submission {
    projectID: number;
    submissionDate: string;
}

interface Certification {
    projectID: number;
    certificationDate: string;
}

interface Expected {
    projectID: number;
    expectedDate: string;
}

type intervalType = 'submission' | 'certification' | 'expected';

const isValidDate = (dateString: Dayjs | string) => {
    return dayjs(dateString, 'DD-MM-YYYY', true).isValid();
};

const AFPTable: React.FC<AFPTableProps> = ({ data, endDate, onSaveInterval }) => {
    const [updatedRowIndex, setUpdatedRowIndex] = useState({
        index: 0,
        isOpen: false,
        startDate: null
    });

    const [intevalDialog, setIntervalDialog] = useState({
        isOpen: false,
        type: ''
    });

    const [dynamicRow, setDynamicRow] = useState<DynamicRow>({
        submissionRows: [],
        certificationRows: [],
        expectedRows: []
    });
    const [originalRow, setOriginalRow] = useState<DynamicRow>({
        submissionRows: [],
        certificationRows: [],
        expectedRows: []
    });

    const [formData, setFormData] = useState<FormData>({
        submissionDate: null,
        certificationDate: null,
        expectedDate: null,
        submissionInterval: 0,
        certificationInterval: 0,
        expectedInterval: 0
    })

    const { projectId } = useParams<{ projectId?: string }>(); // Get projectId from URL

    useEffect(() => {
        if (data) {
            setDynamicRow({
                submissionRows: data.submissionDates,
                certificationRows: data.certificationDates,
                expectedRows: data.expectedPaymentDates
            })

            // Save a copy as original
            setOriginalRow({
                submissionRows: data.submissionDates,
                certificationRows: data.certificationDates,
                expectedRows: data.expectedPaymentDates
            });

            setFormData({
                submissionDate: data.submissionDates[0] || null,
                certificationDate: data.certificationDates[0] || null,
                expectedDate: data.expectedPaymentDates[0] || null,
                submissionInterval: data.submissionInterval,
                certificationInterval: data.certificationInterval,
                expectedInterval: data.paymentInterval
            })
        }

    }, []);


    const generateDateRange = (
        interval: number,
        startDate: Dayjs,
        option: 'none' | 'only' | 'all' = 'none'
    ) => {
        if (option === 'only') return dynamicRow.submissionRows;

        const projectEndDate = dayjs(endDate).add(3, 'month');
        const today = dayjs().startOf('day');
        const validDates = dynamicRow.submissionRows || [];

        // 1. Keep only past or today dates
        const pastDates = validDates?.filter(date =>
            dayjs(date).isSameOrBefore(
                option === 'all' && updatedRowIndex.startDate
                    ? dayjs(updatedRowIndex.startDate)
                    : today,
                'day')
        );

        // 2. Use last past date or startDate as base
        let baseDate: Dayjs;
        let pastDatesResult: string[] = [];

        if (pastDates.length > 0) {
            baseDate = dayjs(pastDates[pastDates.length - 1]);
            const lastDate = dayjs(pastDates[pastDates.length - 2]);
            pastDatesResult = baseDate && lastDate && dayjs(baseDate).isSame(lastDate, 'day')
                ? pastDates.slice(0, -1)
                : pastDates;
        } else {
            baseDate = startDate;
            pastDatesResult = [startDate.format("YYYY-MM-DD")]; // ✅ Add startDate if no past dates
        }

        // 3. Generate future dates based on interval
        const newDates: string[] = [];
        let currentDate = baseDate.add(interval, 'day');

        while (currentDate.isSameOrBefore(projectEndDate)) {
            newDates.push(currentDate.format("YYYY-MM-DD"));
            currentDate = currentDate.add(interval, 'day');
        }

        return [...pastDatesResult, ...newDates];
    };

    const handleDates = (
        type: Array<'submission' | 'certification' | 'expected'>,
        option: 'only' | 'all' | 'none' = 'none' // update all future date or single date
    ) => {

        let submissionResult: Submission[] = [];
        let certificationResult: Certification[] = [];
        let expectedResult: Expected[] = [];

        let submissionDates = [] = dynamicRow.submissionRows;
        let certificationDates = [] = dynamicRow.certificationRows;


        if (type.includes('submission')) {
            const startDate = dayjs(formData.submissionDate);
            submissionDates = generateDateRange(Number(formData.submissionInterval), startDate, option);

            setDynamicRow(prev => ({
                ...prev,
                submissionRows: submissionDates
            }));

            submissionResult = submissionDates.map(date => ({
                projectID: Number(projectId),
                submissionDate: date
            }));
        }

        if (type.includes('certification')) {

            certificationDates = submissionDates.map(date =>
                dayjs(date).add(Number(formData.certificationInterval), 'day').format("YYYY-MM-DD")
            );

            setFormData(prev => ({
                ...prev,
                certificationDate: certificationDates[0]
            }));

            setDynamicRow(prev => ({
                ...prev,
                certificationRows: certificationDates
            }));

            certificationResult = certificationDates.map(date => ({
                projectID: Number(projectId),
                certificationDate: date
            }));
        }

        if (type.includes('expected')) {
            const expectedDates = certificationDates.map(date =>
                dayjs(date).add(Number(formData.expectedInterval), 'day').format("YYYY-MM-DD")
            );

            setFormData(prev => ({
                ...prev,
                expectedDate: expectedDates[0]
            }));

            setDynamicRow(prev => ({
                ...prev,
                expectedRows: expectedDates
            }));

            expectedResult = expectedDates.map(date => ({
                projectID: Number(projectId),
                expectedDate: date
            }));
        }

        const payload: PaymentRequest = {
            projectId: Number(projectId),
            submissionDates: submissionResult,
            certificationDates: certificationResult,
            expectedPaymentDates: expectedResult,
            submissionInterval: formData.submissionInterval,
            certificationInterval: formData.certificationInterval,
            expectedInterval: formData.expectedInterval,
        };

        onSaveInterval(payload)
    }

    const handleSubmissionDates = async () => {
        if (formData.submissionDate
            // && isValidDate(formData.submissionDate)
            && formData.submissionInterval > 0) {

            if (!data || data.submissionInterval === 0) {
                handleDates(['submission'])
                return;
            }
            if (data.submissionInterval > 0 && data.submissionInterval != formData.submissionInterval) {
                setIntervalDialog({
                    isOpen: true,
                    type: 'submission'
                })
            }
        }
    };

    const handleCertificateDates = async () => {
        if (data.certificationInterval === 0) {
            handleDates(['certification'])
            return;
        }
        if (data.certificationInterval > 0 && data.certificationInterval != formData.certificationInterval) {
            setIntervalDialog({
                isOpen: true,
                type: 'certification'
            })
        }
    }

    const handleExpectedDates = () => {
        if (data.paymentInterval === 0) {
            handleDates(['expected'])
            return;
        }
        if (data.paymentInterval > 0 && data.paymentInterval != formData.expectedInterval) {
            setIntervalDialog({
                isOpen: true,
                type: 'expected'
            })
        }
    }

    const handleIntervalDialogCancel = () => {
        setIntervalDialog({
            isOpen: false,
            type: ''
        })
    };

    const handleIntervalDialogConfirm = () => {
        const { type } = intevalDialog;
        const allowType = buildAllowTypes(type as intervalType); // base type
        handleDates(allowType)
        handleIntervalDialogCancel();
    }

    // update all future date
    const handleUpdateAllDate = () => {
        const allowType = buildAllowTypes('submission');
        handleDates(allowType, 'all')
        handleUpdateDateCancel();
    }

    // only update single chnaged date
    const handleUpdateDate = () => {
        const allowType = buildAllowTypes('submission');
        handleDates(allowType, 'only')
        handleUpdateDateCancel();
    }

    const buildAllowTypes = (base: intervalType): intervalType[] => {
        const allowType: intervalType[] = [base];

        if (base !== 'certification' && dynamicRow.certificationRows?.length > 0) {
            allowType.push('certification');
        }

        if (dynamicRow.expectedRows?.length > 0) {
            allowType.push('expected');
        }

        return allowType;
    };

    const handleUpdateDateCancel = () => {
        setUpdatedRowIndex({
            index: 0,
            isOpen: false,
            startDate: null
        })
    }

    return (
        <Box
            mt={4}
            mb={7}
            sx={{
                overflowX: 'auto',
                boxShadow: '5px 5px 10px rgba(0, 0, 0, 0.35)',
                border: '1px solid #ccc',
                borderRadius: '12px',
                backgroundColor: 'white',
                padding: '10px',
            }}
        >
            <Table sx={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={styles.header}>AFP Submission Dates</TableCell>
                        <TableCell sx={{ ...styles.header, borderRight: '2px solid #ccc' }}>Interval Days</TableCell>
                        <TableCell sx={styles.header}>AFP Certificate Dates</TableCell>
                        <TableCell sx={{ ...styles.header, borderRight: '2px solid #ccc' }}>Interval Days</TableCell>
                        <TableCell sx={styles.header}>Expected Payment Dates</TableCell>
                        <TableCell sx={styles.header}>Interval Days</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {/* first row (manual entry) */}
                    <TableRow>
                        <TableCell sx={styles.cell}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <CustomDatePicker
                                    value={formData.submissionDate ? dayjs(formData.submissionDate) : null}
                                    disableOldDate
                                    disabled={dayjs(formData.submissionDate).isBefore(dayjs(), 'day')}
                                    onChange={(newValue) => setFormData({ ...formData, submissionDate: newValue })}
                                    sx={{ mb: 1, height: '35px' }}
                                />
                            </Box>
                        </TableCell>

                        <TableCell sx={{ ...styles.cell, borderRight: '2px solid #ccc' }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <CustomTextField
                                    type="number"
                                    label=""
                                    name="submissionInterval"
                                    value={formData.submissionInterval}
                                    onChange={(e) => setFormData({ ...formData, submissionInterval: Number(e.target.value) })}
                                    onFocus={() =>
                                        setFormData({
                                            ...formData,
                                            submissionInterval: NaN
                                        })
                                    }
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={styles.input}
                                />

                                <CustomButton
                                    capsule
                                    onClick={handleSubmissionDates}
                                    sx={{ fontSize: '14px', height: '36px', width: '100%', whiteSpace: 'nowrap' }}
                                >
                                    Add Interval Days
                                </CustomButton>
                            </Box>
                        </TableCell>


                        {/* AFP Certificate Date (disabled DatePicker) */}
                        <TableCell sx={styles.cell}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <CustomDatePicker
                                    value={formData.certificationDate ? dayjs(formData.certificationDate) : null}
                                    onChange={(newValue) => setFormData({ ...formData, certificationDate: newValue })}
                                    sx={{ mb: 1, height: '35px' }}
                                    disabled
                                />
                            </Box>
                        </TableCell>

                        <TableCell sx={{ ...styles.cell, borderRight: '2px solid #ccc' }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <CustomTextField
                                    type="number"
                                    label=""
                                    name="certificationInterval"
                                    value={formData.certificationInterval}
                                    onChange={(e) => setFormData({ ...formData, certificationInterval: Number(e.target.value) })}
                                     onFocus={() =>
                                        setFormData({
                                            ...formData,
                                            certificationInterval: NaN
                                        })
                                    }
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={styles.input}
                                />

                                {dynamicRow.submissionRows.length > 0 && (
                                    <CustomButton
                                        capsule
                                        onClick={handleCertificateDates}
                                        sx={{ fontSize: '14px', height: '36px', width: '100%', whiteSpace: 'nowrap' }}
                                    >
                                        Add Interval Days
                                    </CustomButton>
                                )}
                            </Box>
                        </TableCell>

                        {/* Expected Payment Date (disabled DatePicker) */}
                        <TableCell sx={styles.cell}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <CustomDatePicker
                                    value={formData.expectedDate ? dayjs(formData.expectedDate) : null}
                                    onChange={(newValue) => setFormData({ ...formData, expectedDate: newValue })}
                                    sx={{ mb: 1, height: '35px' }}
                                    disabled
                                />
                            </Box>
                        </TableCell>

                        <TableCell sx={{ ...styles.cell, borderRight: '2px solid #ccc' }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <CustomTextField
                                    type="number"
                                    label=""
                                    name="expectedInterval"
                                    value={formData.expectedInterval}
                                    onChange={(e) => setFormData({ ...formData, expectedInterval: Number(e.target.value) })}
                                     onFocus={() =>
                                        setFormData({
                                            ...formData,
                                            expectedInterval: NaN
                                        })
                                    }
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={styles.input}
                                />

                                {dynamicRow.certificationRows.length > 0 && (
                                    <CustomButton
                                        capsule
                                        onClick={handleExpectedDates}
                                        sx={{ fontSize: '14px', height: '36px', width: '100%', whiteSpace: 'nowrap' }}
                                    >
                                        Add Interval Days
                                    </CustomButton>
                                )}

                            </Box>
                        </TableCell>
                    </TableRow>

                    {/* dynamic row based on generated submission date */}
                    {dynamicRow.submissionRows &&
                        dynamicRow.submissionRows.map((date, index) => {
                            if (index === 0) return null;

                            return (
                                <TableRow key={index}>

                                    {/* AFP Submission Date */}
                                    <TableCell sx={styles.cell}>
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            <CustomDatePicker
                                                value={dayjs(date)}
                                                onChange={(newValue) => {
                                                    const updatedSubmissionRows = [...dynamicRow.submissionRows];
                                                    updatedSubmissionRows[index] = dayjs(newValue).format("YYYY-MM-DD");
                                                    setUpdatedRowIndex({
                                                        index: index,
                                                        isOpen: false,
                                                        startDate: newValue
                                                    });
                                                    setDynamicRow(prev => ({
                                                        ...prev,
                                                        submissionRows: updatedSubmissionRows
                                                    }));
                                                }}
                                                sx={{ mb: 1, height: '35px' }}
                                                disabled={dayjs(date).isBefore(dayjs(), 'day')}
                                            />

                                            {updatedRowIndex.index === index && (
                                                <Box display="flex" flexDirection="row" gap={1} justifyContent="center">
                                                    <CustomButton
                                                        capsule
                                                        onClick={() => {
                                                            setUpdatedRowIndex(prev => ({
                                                                ...prev,
                                                                isOpen: true
                                                            }))
                                                        }}
                                                        sx={{
                                                            fontSize: '14px',
                                                            height: '36px',
                                                            whiteSpace: 'nowrap',
                                                            px: 2,
                                                            minWidth: '120px'
                                                        }}
                                                    >
                                                        Update Date
                                                    </CustomButton>

                                                    <CustomButton
                                                        capsule
                                                        //variant="outlined"
                                                        onClick={() => {
                                                            // Reset to old/original date
                                                            const resetRows = [...dynamicRow.submissionRows];
                                                            resetRows[index] = originalRow.submissionRows[index]; // restore old value

                                                            setDynamicRow(prev => ({
                                                                ...prev,
                                                                submissionRows: resetRows
                                                            }));

                                                            // Clear update tracking
                                                            setUpdatedRowIndex({
                                                                index: 0,
                                                                isOpen: false,
                                                                startDate: null
                                                            });
                                                        }}
                                                        sx={{
                                                            fontSize: '14px',
                                                            height: '36px',
                                                            whiteSpace: 'nowrap',
                                                            px: 2,
                                                            minWidth: '120px',
                                                            backgroundColor: '#f5f5f5',
                                                            //color: '#555'
                                                        }}
                                                    >
                                                        Reset Date
                                                    </CustomButton>
                                                </Box>
                                            )}



                                        </Box>

                                    </TableCell>
                                    <TableCell sx={{ ...styles.cell, borderRight: '2px solid #ccc' }}>

                                    </TableCell>


                                    {/* AFP Certificate Date (disabled DatePicker) */}
                                    <TableCell sx={styles.cell}>
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            <CustomDatePicker
                                                value={
                                                    dynamicRow.certificationRows[index]
                                                        ? dayjs(dynamicRow.certificationRows[index])
                                                        : null
                                                }
                                                sx={{ mb: 1, height: '35px' }}
                                                disable
                                            />

                                        </Box>
                                    </TableCell>

                                    <TableCell sx={{ ...styles.cell, borderRight: '2px solid #ccc' }}>
                                    </TableCell>

                                    {/* Expected Payment Date (disabled DatePicker) */}
                                    <TableCell sx={styles.cell}>
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            <CustomDatePicker
                                                value={
                                                    dynamicRow.expectedRows[index]
                                                        ? dayjs(dynamicRow.expectedRows[index])
                                                        : null
                                                }
                                                sx={{ mb: 1, height: '35px' }}
                                                disable
                                            />
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={{ ...styles.cell, borderRight: '2px solid #ccc' }}>
                                    </TableCell>

                                </TableRow>
                            );
                        })}

                </TableBody>
            </Table>

            <CustomConfirmDialog
                open={intevalDialog.isOpen}
                title="Change Interval"
                message={validationMessages.project_AFP_IntervalChange(intevalDialog.type)}
                onConfirm={handleIntervalDialogConfirm}
                onCancel={handleIntervalDialogCancel}
                onClose={handleIntervalDialogCancel}
            />

            <CustomConfirmDialog
                open={updatedRowIndex.isOpen}
                title="Change Date?"
                message="Do you want to update only this date or this and all future dates?"
                onConfirm={handleUpdateAllDate} // All future dates
                onCancel={handleUpdateDate}  // Only this date
                onClose={handleUpdateDateCancel}
                confirmLabel='All Future Dates'
                cancelLabel='Only This Date'
            />

        </Box>
    );
};

const styles = {
    header: {
        color: '#1e66ab',
        fontWeight: 400,
        fontSize: '16px',
        textAlign: 'center',
        borderBottom: '2px solid #ccc',
        '&::after': {
            content: '" *"',
            color: 'red',
            fontWeight: 400,
        },
    },
    cell: {
        padding: '12px',
        textAlign: 'center',
    },
    input: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '35px',
            height: '40px',
        },
        '& .MuiInputBase-input': {
            paddingLeft: '15px',
            fontSize: '14px',
            height: '100%',
        },
    },
};

export default AFPTable;